import axios from 'axios';
import { getData } from '../utils/apiResponse';
import axiosRetry from 'axios-retry';
import Cookies from 'js-cookie';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Global axios interceptor — applies to ALL axios calls,
 * including raw axios.post() calls in userService.js.
 * Reads saved language from localStorage and adds Accept-Language header.
 */
axios.interceptors.request.use(
  (config) => {
    const language = localStorage.getItem('language') || 'en';
    config.headers['Accept-Language'] = language;
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Axios instance configured for API requests with authentication.
 * Automatically adds JWT token from cookies and language header.
 * Base URL is set from REACT_APP_API_URL environment variable.
 * Retries up to 3 times with exponential backoff on network errors
 * and server errors (5xx), which are common on unstable mobile connections.
 */
const api = axios.create({
  baseURL: API_URL,
});

// Retry up to 3 times with exponential backoff.
// Covers network timeouts and 5xx server errors.
// Does NOT retry 4xx errors (bad request, unauthorized) — those are not transient.
axiosRetry(api, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) =>
    axiosRetry.isNetworkError(error) || axiosRetry.isRetryableError(error),
});

api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('jwt');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const language = localStorage.getItem('language') || 'en';
    config.headers['Accept-Language'] = language;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token expiration with silent refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = Cookies.get('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');

        const response = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        const authData = getData(response);
        const newAccessToken = authData.accessToken;
        const newRefreshToken = authData.refreshToken;

        Cookies.set('jwt', newAccessToken, { expires: 1 });
        if (newRefreshToken) Cookies.set('refreshToken', newRefreshToken, { expires: 7 });

        api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        Cookies.remove('jwt');
        Cookies.remove('refreshToken');
        Cookies.remove('userRole');
        Cookies.remove('healthFacility');
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
